import React, { useState, useCallback } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth, useCurrentTime, useOnlineStatus, useProjects, useTimeEntries, useTimer } from './hooks';
import { formatDateTimeLocal } from './utils/formatters';
import {
  AdminView,
  Header,
  LoadingScreen,
  LoginScreen,
  NewEntryModal,
  PendingScreen,
  ProjectModal,
  ProjectsView,
  RejectedScreen,
  ReportsView,
  SettingsView,
  TabNav,
  TimelineView,
  TimerView
} from './components';

export default function TimeTracker() {
  // Auth
  const { user, loading, userStatus, isApproved, isAdmin, signIn, signOut } = useAuth();

  // Core state
  const isOnline = useOnlineStatus();
  const currentTime = useCurrentTime();

  // Data hooks
  const {
    projects,
    addProject,
    updateProject,
    deleteProject,
    getProjectById
  } = useProjects(user, isOnline);

  const {
    entries,
    syncQueue,
    addEntry,
    updateEntry,
    deleteEntry,
    getTodayEntries,
    clearUserData
  } = useTimeEntries(user, isOnline);

  // Timer hook
  const handleTimerStop = useCallback((completedEntry) => {
    addEntry(completedEntry);
  }, [addEntry]);

  const {
    currentTimer,
    startTimer,
    updateTimer,
    updateTimerStartTime,
    stopTimer,
    clearTimer
  } = useTimer(user, isOnline, handleTimerStop);

  // UI state
  const [description, setDescription] = useState('');
  const [selectedProject, setSelectedProject] = useState('no-project');
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [newEntryForm, setNewEntryForm] = useState(null);

  // Edit state
  const [editingEntry, setEditingEntry] = useState(null);
  const [editForm, setEditForm] = useState({
    description: '',
    startTime: '',
    endTime: '',
    projectId: 'no-project'
  });

  // Handlers
  const handleSignOut = async () => {
    await signOut();
    clearUserData();
    clearTimer();
  };

  const handleStartTimer = () => {
    startTimer(description, selectedProject);
    setDescription('');
  };

  const handleAddProject = (name, color) => {
    addProject(name, color);
  };

  const handleStartEdit = (entry) => {
    setEditingEntry(entry.id);
    setEditForm({
      description: entry.description,
      startTime: formatDateTimeLocal(entry.startTime),
      endTime: entry.endTime ? formatDateTimeLocal(entry.endTime) : '',
      projectId: entry.projectId || 'no-project'
    });
  };

  const handleCancelEdit = () => {
    setEditingEntry(null);
    setEditForm({
      description: '',
      startTime: '',
      endTime: '',
      projectId: 'no-project'
    });
  };

  const handleSaveEdit = async () => {
    if (!editingEntry) return;

    const updates = {
      description: editForm.description.trim(),
      startTime: new Date(editForm.startTime).getTime(),
      endTime: editForm.endTime ? new Date(editForm.endTime).getTime() : null,
      projectId: editForm.projectId
    };

    await updateEntry(editingEntry, updates);
    handleCancelEdit();
  };

  const handleGapClick = (startTime, endTime) => {
    setNewEntryForm({
      description: '',
      startTime: formatDateTimeLocal(startTime),
      endTime: formatDateTimeLocal(endTime),
      projectId: 'no-project'
    });
  };

  const handleSaveNewEntry = async () => {
    if (!newEntryForm) return;

    const startTime = new Date(newEntryForm.startTime).getTime();
    const endTime = new Date(newEntryForm.endTime).getTime();

    if (startTime >= endTime) {
      alert('End time must be after start time');
      return;
    }

    const newEntry = {
      id: Date.now(),
      description: newEntryForm.description.trim(),
      startTime,
      endTime,
      projectId: newEntryForm.projectId
    };

    await addEntry(newEntry);
    setNewEntryForm(null);
  };

  // Loading state
  if (loading) {
    return <LoadingScreen />;
  }

  // Login state
  if (!user) {
    return <LoginScreen onSignIn={signIn} />;
  }

  // Check approval status (admins bypass this)
  if (!isApproved && !isAdmin) {
    if (userStatus === 'pending') {
      return <PendingScreen user={user} onSignOut={handleSignOut} />;
    }
    if (userStatus === 'rejected') {
      return <RejectedScreen user={user} onSignOut={handleSignOut} />;
    }
    // If no status yet, show loading
    return <LoadingScreen />;
  }

  // Main app
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <Header
            user={user}
            isOnline={isOnline}
            syncQueueLength={syncQueue.length}
            onSignOut={handleSignOut}
          />

          <TabNav isAdmin={isAdmin} />

          <div className="p-6">
            <Routes>
              <Route path="/" element={<Navigate to="/timer" replace />} />

              <Route
                path="/timer"
                element={
                  <TimerView
                    description={description}
                    setDescription={setDescription}
                    selectedProject={selectedProject}
                    setSelectedProject={setSelectedProject}
                    projects={projects}
                    currentTimer={currentTimer}
                    currentTime={currentTime}
                    entries={entries}
                    editingEntry={editingEntry}
                    editForm={editForm}
                    getProjectById={getProjectById}
                    onStartTimer={handleStartTimer}
                    onStopTimer={stopTimer}
                    onUpdateTimer={updateTimer}
                    onUpdateTimerStartTime={updateTimerStartTime}
                    onStartEdit={handleStartEdit}
                    onSaveEdit={handleSaveEdit}
                    onCancelEdit={handleCancelEdit}
                    onEditFormChange={setEditForm}
                    onDeleteEntry={deleteEntry}
                    onShowProjectModal={() => setShowProjectModal(true)}
                  />
                }
              />

              <Route
                path="/timeline"
                element={
                  <TimelineView
                    todayEntries={getTodayEntries()}
                    currentTimer={currentTimer}
                    currentTime={currentTime}
                    projects={projects}
                    editingEntry={editingEntry}
                    editForm={editForm}
                    getProjectById={getProjectById}
                    onEditFormChange={setEditForm}
                    onStartEdit={handleStartEdit}
                    onSaveEdit={handleSaveEdit}
                    onCancelEdit={handleCancelEdit}
                    onDeleteEntry={deleteEntry}
                    onUpdateTimer={updateTimer}
                    onUpdateTimerStartTime={updateTimerStartTime}
                    onGapClick={handleGapClick}
                  />
                }
              />

              <Route
                path="/reports"
                element={
                  <ReportsView
                    entries={entries}
                    projects={projects}
                    getProjectById={getProjectById}
                  />
                }
              />

              <Route
                path="/projects"
                element={
                  <ProjectsView
                    projects={projects}
                    onAddProject={addProject}
                    onUpdateProject={updateProject}
                    onDeleteProject={deleteProject}
                  />
                }
              />

              <Route
                path="/settings"
                element={<SettingsView />}
              />

              <Route
                path="/admin"
                element={isAdmin ? <AdminView /> : <Navigate to="/timer" replace />}
              />
            </Routes>
          </div>
        </div>
      </div>

      {newEntryForm && (
        <NewEntryModal
          form={newEntryForm}
          projects={projects}
          onChange={setNewEntryForm}
          onSave={handleSaveNewEntry}
          onCancel={() => setNewEntryForm(null)}
        />
      )}

      {showProjectModal && (
        <ProjectModal
          onClose={() => setShowProjectModal(false)}
          onAddProject={handleAddProject}
        />
      )}
    </div>
  );
}
